<template>
  <ValidationObserver v-if="item" tag="form" ref="form"  v-slot="{ validate, errors, failed }">
    <b-form @submit.prevent>
      <AdminControlBar
        v-if="role === 'admin'"
        v-model="item"
        :readOnly="readOnly"
        @edit-click="toggleReadOnly"
        @cancel-click="onCancel($event)"
        v-stream:save-click="onResubmit$"
        :title="title"
        :validate="validate"
      >
      </AdminControlBar>
      <MerchantControlBar
        v-else
        v-model="item"
        :formRef="$refs.form"
        :readOnly="readOnly"
        @edit-click="toggleReadOnly"
        @cancel-click="onCancel($event)"
        @save-draft-click="onSaveDraftClick"
        v-stream:preview-click="onPreview$"
        :couponStateBeforeUpdate.sync="couponStateBeforeUpdate"
        :title="title"
      ></MerchantControlBar>
      <div class="container-fluid">
        <div class="coupon-form row">
          <div class="col-12 col-md-9">
            <div class="coupon-form__body">
              <transition name="fade">
                <NotificationMessage :messages="errors" v-if="failed"></NotificationMessage>
              </transition>
              <NotificationMessage variant="info"
                >優惠券刊登後，閣下可到U Lifestyle App的「U
                Jetso」頁面搜尋該優惠券，並於禮遇詳情右上角按鈕取得分享連結。
              </NotificationMessage>
              <StatusCard
                v-if="item.currentStatus && item.currentStatus.status !== 'DRAFT'"
                :status="item.currentStatus.status"
                :id="item.reward.id"
              >
                <template v-slot:header>
                  <CouponStockCard :id="item.reward.id"></CouponStockCard>
                </template>
              </StatusCard>
              <BasicInfo
                v-model="item"
                :readOnly="readOnly"
                :tagOptions="tagOptions$"
                :cmsTagOptions="cmsTagOptions$"
                :isUFUN="isUFUN"
              ></BasicInfo>
              <CouponInfo v-model="item" :readOnly="readOnly" :isUFUN="isUFUN"></CouponInfo>
              <CustomQuestion v-model="item" :readOnly="readOnly" :isUFUN="isUFUN">
              </CustomQuestion>
              <PublishSetting v-model="item" :readOnly="readOnly"></PublishSetting>
            </div>
          </div>
          <div class="col-2 hidden-phone">
            <b-nav class="coupon-form__scroll-spy" v-b-scrollspy="200" vertical>
              <b-nav-item href="#anchor-info">基本資料</b-nav-item>
              <b-nav-item href="#anchor-coupon">優惠碼資料</b-nav-item>
              <b-nav-item href="#anchor-settings">發佈設定</b-nav-item>
            </b-nav>
          </div>
        </div>
      </div>
    </b-form>
    <CouponPreviewModal
      id="coupon-preview-modal"
      :item="item"
      :tagOptions="tagOptions$"
      v-stream:submit-click="onSubmit$"
      v-stream:resubmit-click="{ subject: onResubmit$, data: 'RESUBMIT' }"
    >
    </CouponPreviewModal>
    <BaseModal
      @ok-click="modal.okAction"
      :title="modal.title"
      :is-open="modal.isShow"
      @cancel-click="modal.cancelAction"
      @hidden="modal.isShow = false"
      size="sm">
    </BaseModal>
    <LoadingSpinner v-show="loading"></LoadingSpinner>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { of, Subject } from 'rxjs';
import {
  pluck, startWith, switchMap, tap, map, filter,
} from 'rxjs/operators';
import {
  BNav, BNavItem, VBScrollspy, BForm,
} from 'bootstrap-vue';
import cloneDeep from 'lodash.clonedeep';
import BaseModal from '@/components/BaseActionModal.vue';
import toastSettings from '../../helpers/toast-settings';
import http from '../../helpers/http';
import { formatDate } from '../../helpers/date';
import { getErrorMsg } from '../../helpers/error-msg';
import formMixin from '../../mixins/form';
import LoadingSpinner from '../../components/LoadingSpinner.vue';
import AdminControlBar from '../../components/coupon/AdminControlBar.vue';
import StatusCard from '../../components/StatusCard.vue';
import CouponStockCard from '../../components/coupon/CouponStockCard.vue';
import MerchantControlBar from '../../components/coupon/MerchantControlBar.vue';
import BasicInfo from '../../components/coupon/BasicInfo.vue';
import CouponInfo from '../../components/coupon/CouponInfo.vue';
import CustomQuestion from '../../components/coupon/CustomQuestion.vue';
import PublishSetting from '../../components/coupon/PublishSetting.vue';
import CouponPreviewModal from '../../components/coupon/CouponPreviewModal.vue';
import NotificationMessage from '../../components/NotificationMessage.vue';

const initialState = {
  reward: {
    name1: '',
    desc: '',
    applicableAddressOrWebsite: '',
    maxRedeem: 0,
    tnc: '',
    publishOn: null,
    publishDue: null,
    quantity: 0,
    relatedLinkUrl: '',
    tagOnCover: '',
    draftData: {
      codes: [],
      quantity: 0,
    },
  },
  covers: {
    ulapp: null,
    hk: 'placeholder/placeholder_UHK.jpg',
    beauty: 'placeholder/placeholder_UB.jpg',
    blog: 'placeholder/placeholder_UBlog.jpg',
    food: 'placeholder/placeholder_UF.jpg',
    travel: 'placeholder/placeholder_UT.jpg',
  },
  images: [],
  tags: [],
  couponMeta: {
    startDate: null,
    expire: null,
    codeType: 'text',
    singleCode: '',
  },
  questions: [
    // no default empty question
    // {
    //   questionType: 'default',
    //   question: null,
    //   hints: null,
    //   compulsory: false,
    // },
  ],
};

export default {
  mixins: [formMixin],
  components: {
    BNav,
    BNavItem,
    AdminControlBar,
    StatusCard,
    MerchantControlBar,
    BForm,
    PublishSetting,
    BasicInfo,
    CouponInfo,
    CustomQuestion,
    CouponPreviewModal,
    LoadingSpinner,
    NotificationMessage,
    CouponStockCard,
    BaseModal,
  },
  data: () => ({
    active: true,
    loading: false,
    readOnly: false,
    title: '',
    couponStateBeforeUpdate: null,
    modal: {
      title: '',
      okAction() {},
      cancelAction() {},
      hiddenAction() {},
    },
  }),
  computed: {
    ...mapState(['user']),
    ...mapGetters(['role']),
    id() {
      return this.$route.params.id;
    },
    merchantName() {
      if (this.role === 'merchant') {
        return this.user.name;
      }
      if (!this.item.merchant) {
        return null;
      }
      return this.item.merchant.name;
    },
    couponType() {
      return this.$route.meta.paymentType === 'UFUN' ? '積分' : '付費';
    },
    isUFUN() {
      return this.$route.meta.paymentType === 'UFUN';
    },
  },
  directives: {
    'b-scrollspy': VBScrollspy,
  },
  domStreams: ['onSubmit$', 'onPreview$', 'onResubmit$', 'onDraftSave$', 'onDraftResumit$'],
  subscriptions() {
    const getData$ = (id) => http({
      method: 'GET',
      url: `/agc-${this.role}-api/coupons/${id}`,
    });

    const submit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/coupons/submit`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: (res) => {
          this.readOnly = true;
          if (this.item.isDraft) {
            this.$router.push(`/${this.$route.path.split('/')[1]}`);
          } else {
            this.$nextTick(() => {
              this.$refs.form.reset();
            });
            this.$bvModal.hide('coupon-preview-modal');
            if (this.role === 'merchant' && res.reward.status === 'PENDING') {
              this.$bvModal.show('coupon-submit-success-modal');
            }
          }
        },
        complete: () => {
          this.loading = false;
        },
      },
    );

    const resubmit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/coupons/resubmit/${this.id}`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: (res) => {
          this.item = cloneDeep(res);
          this.couponStateBeforeUpdate = cloneDeep(res);
          this.title = res.reward.name1;

          this.toggleReadOnly();
          this.$nextTick(() => {
            this.$refs.form.reset();
          });
          this.$bvModal.hide('coupon-preview-modal');
          if (this.role === 'merchant' && res.reward.status === 'PENDING') {
            this.$bvModal.show('coupon-submit-success-modal');
          }
        },
        complete: () => {
          this.loading = false;
        },
      },
    );

    return {
      item: this.$watchAsObservable('id').pipe(
        pluck('newValue'),
        startWith(this.id),
        switchMap((id) => {
          if (!id) {
            return of(
              cloneDeep({
                ...initialState,
                merchant: {
                  name: this.merchantName,
                },
              }),
            );
          }
          return getData$(id);
        }),
        map((res) => ({
          ...res,
          covers: {
            ...initialState.covers,
            ...res.covers,
            ulapp: res.covers.ulapp,
          },
        })),
        tap((res) => {
          const { reward } = res;
          this.title = reward.name1;
          this.couponStateBeforeUpdate = cloneDeep(res);
        }),
      ),
      tagOptions$: http({
        method: 'GET',
        url: `/agc-public-api/tags?isAdmin=${this.role === 'admin'}`,
      }).pipe(
        pluck('result'),
        map((result) => result.map(({ id, name }) => ({ id, name }))),
      ),
      cmsTagOptions$: new Subject().pipe(
        startWith(0),
        filter(() => this.role === 'admin'),
        switchMap(() => http({
          method: 'GET',
          url: '/agc-public-api/tags?cmstag=true',
        }).pipe(
          pluck('result'),
          map((result) => result.map(({ id, name }) => ({ id, name }))),
        )),
      ),
      onSubmit: this.onSubmit$.pipe(
        pluck('data'),
        switchMap((type) => {
          // coupon of DRAFT does not need to validate
          if (type === 'DRAFT') {
            this.item.isDraft = true;
            return of(true);
          }
          this.item.isDraft = false;
          return this.validate({ isScrollToTopIfFail: true });
        }),
        tap(() => {
          this.loading = true;
        }),
        map(() => ({
          ...this.item,
          ...{
            reward: {
              ...this.item.reward,
              paymentType: this.isUFUN ? 'UFUN' : 'HKD',
            },
            couponMeta: {
              ...this.item.couponMeta,
              startDate: formatDate(this.item.couponMeta.startDate, true),
              expire: formatDate(this.item.couponMeta.expire, true),
            },
          },
        })),
        switchMap(submit$),
      ),
      onResubmit: this.onResubmit$.pipe(
        pluck('data'),
        switchMap((type) => {
          // coupon of DRAFT does not need to validate
          if (type === 'DRAFT') {
            this.item.isDraft = true;
            return of(true);
          }
          this.item.isDraft = false;
          return this.validate({ isScrollToTopIfFail: true });
        }),
        tap(() => {
          this.loading = true;
        }),
        map(() => ({
          ...this.item,
          ...{
            reward: {
              ...this.item.reward,
            },
            couponMeta: {
              ...this.item.couponMeta,
              startDate: formatDate(this.item.couponMeta.startDate, true),
              expire: formatDate(this.item.couponMeta.expire, true),
            },
          },
        })),
        switchMap(resubmit$),
      ),
      onPreview: this.onPreview$.pipe(
        switchMap(() => this.validate({ isScrollToTopIfFail: true })),
        tap(() => {
          this.$bvModal.show('coupon-preview-modal');
        }),
      ),
    };
  },
  methods: {
    initialize() {
      this.item = cloneDeep({
        ...initialState,
        merchant: {
          name: this.merchantName,
        },
      });
      this.readOnly = false;
    },
    toggleReadOnly() {
      this.readOnly = !this.readOnly;
    },
    onCancel() {
      this.toggleReadOnly();
      this.$refs.form.reset();
      this.item = cloneDeep({
        ...this.couponStateBeforeUpdate,
        currentStatus: { ...this.item.currentStatus },
      });
    },
    setDefaultDesc() {
      if (this.isUFUN) {
        this.item.reward.desc = `
      <p>按下列連結到網店購買任何產品，於結帳前輸入指定優惠碼，即可享9折優惠。/</p>
      <p>憑電子優惠碼到門市購買指定貨品，於付款前向職員出示，即可享9折優惠。</p>
      <p>請留意：</p>
      <ul>
        <li>成功兌換的電子優惠券將收藏至「電子券」專區。</li>
        <li>請自行滑動此券以取得優惠碼。(須在付款前於優惠碼欄輸入)／請於付款前讓店員滑動以使用優惠碼。</li>
      </ul>
      <p><b>(以上為預設描述，商戶可自行修改)</b></p>`;
        this.item.reward.applicableAddressOrWebsite = '';
      } else {
        this.item.reward.desc = `
      <p>按"立即購買＂，即可以優惠價購買此商品。</p>
      <p>請留意：</p>
      <ul>
        <li>成功購買的電子優惠券將收藏至「電子券」專區。</li>
        <li>請自行滑動此券以取得優惠碼。(須在下單時於優惠碼欄輸入)／</li>
        <li>請到門市讓店員滑動以換領商品。</li>
        <li>換領詳情請參閱下列適用分店地址/ 網址 以及 條款及細則</li>
      </ul>
      <p><b>(以上為預設描述，商戶可自行修改)</b></p>
      `;
        this.item.reward.applicableAddressOrWebsite = `
      <p>用戶可憑電子優惠券於有效日期前到以下門市地點換領產品：／用戶可憑電子優惠券於有效日期前到以下連結下單以安排領取或送貨：</p>
      <p><b>(以上為預設描述，商戶可自行修改)</b></p>
      `;
      }
    },
    onSaveDraftClick() {
      this.$refs.form.reset();
      this.modal = {
        isShow: true,
        title: '確認儲存為草稿？',
        okAction: this.handleDraftSubmitOrResubmit,
        cancelAction: () => {
          this.modal.isShow = false;
          this.initialize();
        },
      };
    },
    handleDraftSubmitOrResubmit() {
      this.modal.isShow = false;
      if (this.item.reward.status === 'DRAFT') {
        return this.onResubmit$.next({ data: 'DRAFT' });
      }
      // new draft
      return this.onSubmit$.next({ data: 'DRAFT' });
    },
  },
  mounted() {
    this.$root.$on('bv::scrollspy::activate', (hash) => {
      if (this.$route.hash !== hash) {
        this.$router.replace({ hash });
      }
    });
    if (this.$route.name === 'EditCoupon' || this.$route.name === 'EditPaymentCoupon') {
      this.readOnly = true;
    }
    this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
      if (modalId === 'coupon-submit-success-modal' && this.$route.name.toLowerCase().includes('add')) {
        this.initialize();
      }
    });
    this.title = `建立${this.couponType}優惠券`;
    if (this.item) {
      this.setDefaultDesc();
    }
  },
  watch: {
    couponType(value) {
      this.title = `建立${value}優惠券`;
      this.setDefaultDesc();
    },
  },
  beforeDestroy() {
    this.initialize();
  },
  beforeRouteLeave(to, from, next) {
    if (this.readOnly) {
      this.initialize();
      next();
    } else if (this.role === 'admin') {
      const answer = window.confirm('系統可能不會儲存你所做的變更。');
      if (answer) {
        this.initialize();
        next();
      }
    } else if (
      (this.$route.name.toLowerCase().includes('add'))
      && this.item.reward.status !== 'REMOVED'
    ) {
      // handle back button is clicked:
      // new coupon or draft is editing
      this.$refs.form.reset();
      this.modal = {
        title: '需要儲存為草稿？',
        isShow: true,
        okAction: () => {
          this.handleDraftSubmitOrResubmit();
        },
        cancelAction: () => {
          this.modal.isShow = false;
          this.initialize();
          next();
        },
      };
    } else {
      next();
    }
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/scss/variables';
@import '@/assets/scss/transition/fade';

body {
  position: relative;
}

.coupon-form {
  width: 84%;
  margin: 0 auto;
  justify-content: space-between;
  overflow: hidden;

  @media (max-width: 765px) {
    width: 100% !important;
    & .hidden-phone {
      display: none;
    }
  }

  & .input-section-block {
    background: #fff;
    padding: 0 24px;
    margin: 8px 0;
    box-sizing: border-box;
    border: 1px solid #cae4fc;
    border-radius: 10px;
    overflow: hidden;
    color: #636d77;
    & h3 {
      font-size: 18px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 25px;
      padding: 22px 0;
      margin: 0;
    }
  }

  &__body {
    position: relative;
  }

  &__scroll-spy {
    position: fixed;
    top: 220px;

    &::v-deep {
      $scrollspy-color-active: #174169;

      & .nav-item {
        border-left: 2px solid #dde1e5;

        &.active {
          border-left-color: $scrollspy-color-active;
        }
      }

      & .nav-link {
        padding: 5px 16px;
        color: #636d77;
        font-size: 14px;

        &.active {
          color: $scrollspy-color-active;
        }
      }
    }
  }
}

.form-card {
  display: flex;
  flex-direction: column;
}

.container-fluid {
  @include respond(phone) {
    width: 100%;
  }
}

@import '../../assets/scss/variables';
@import '../../assets/scss/coupon-form';
</style>
